import React from "react";
import { Star, Lock, ShieldCheck, MapPin, Briefcase, Award, Zap, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import MatchScoreCard from "@/components/shared/MatchScoreCard";
import QualityBadges from "@/components/shared/QualityBadges";

export function StarRating({ rating = 0, count = 0 }) {
  const rounded = Math.round(rating);
  return (
    <div className="flex items-center gap-1 text-sm">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= rounded ? "star-full fill-current" : "star-empty"}`} />
      ))}
      <span className="text-white/60 ml-1 font-manrope">({count})</span>
    </div>
  );
}

export function ProfessionalCard({ p, linkPrefix = "/company/professionals" }) {
  // Preserve current URL query so score context flows to profile detail
  const search = typeof window !== "undefined" ? window.location.search : "";
  return (
    <div className="card-flat p-5 flex flex-col gap-3 reveal" data-testid={`prof-card-${p.secusource_id}`}>
      <div className="flex items-center justify-between">
        <span className="ss-id" data-testid={`prof-id-${p.secusource_id}`}>{p.secusource_id}</span>
        <div className="flex items-center gap-1">
          {p.priority_boosted && (
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-[10px] font-outfit font-bold uppercase tracking-widest" title="Priority boosted via referrals">
              <Zap className="w-3 h-3" /> Top Pick
            </span>
          )}
          {p.verified && (
            <span className="verified-chip">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} /> Verified
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-3 items-start">
        <div className="lock-chip" title="Identity hidden until booking">
          <Lock className="w-3.5 h-3.5" />
          Anonymous
        </div>
      </div>

      <div className="text-white/70 font-manrope text-sm flex flex-wrap gap-x-4 gap-y-1">
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-white/50" /> {p.county || "—"}</span>
        <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5 text-white/50" /> {p.years_experience || 0} yr exp</span>
      </div>

      {/* Country availability + licence chips */}
      <div className="flex flex-wrap gap-1">
        {(p.countries_available || []).map((c) => (
          <span key={c} className={`chip ${c === "UK" ? "bg-indigo-50 text-indigo-700 border-indigo-100" : "bg-gold-50 text-gold-600 border-gold-100"}`} data-testid={`prof-country-${c}`}>
            {c === "UK" ? "🇬🇧 UK" : "🇮🇪 IE"}
          </span>
        ))}
        {p.psa_licence_status === "verified" && <span className="chip bg-blue-50 text-blue-700 border-blue-100">PSA ✓</span>}
        {p.psa_licence_status === "expired" && <span className="chip bg-red-50 text-red-700 border-red-100">PSA expired</span>}
        {p.sia_licence_status === "verified" && <span className="chip bg-blue-50 text-blue-700 border-blue-100">SIA ✓</span>}
        {p.sia_licence_status === "expired" && <span className="chip bg-red-50 text-red-700 border-red-100">SIA expired</span>}
        {p.cp_qualification && <span className="chip">CP</span>}
        {p.first_aid_qualification && <span className="chip">First Aid</span>}
        {p.willing_cross_border && <span className="chip bg-amber-50 text-amber-700 border-amber-100">Cross-border</span>}
      </div>

      <div className="flex flex-wrap gap-1">
        {(p.role_categories || []).slice(0, 3).map((r) => (
          <span key={r} className="chip">{r}</span>
        ))}
      </div>

      {/* Runtime status badges — vetting / on job / in team / in deployment */}
      <div className="flex flex-wrap gap-1.5" data-testid={`prof-state-${p.secusource_id}`}>
        {p.vetting_state === "fully_vetted" && p.verified && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-500 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
            <ShieldCheck className="w-3 h-3" /> Fully vetted
          </span>
        )}
        {p.vetting_state === "provisionally_vetted" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
            <ShieldCheck className="w-3 h-3" /> Provisionally vetted
          </span>
        )}
        {p.in_deployment && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> In deployment
          </span>
        )}
        {!p.in_deployment && p.on_job && (
          <span className="inline-flex items-center gap-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> On a job
          </span>
        )}
        {p.in_team && (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 text-[10px] uppercase tracking-widest font-outfit font-bold px-2 py-0.5">
            <Award className="w-3 h-3" /> In a team
          </span>
        )}
      </div>

      {(p.kit || []).length > 0 && (
        <div className="text-xs text-white/60 font-manrope">
          <Award className="inline w-3 h-3 mr-1" /> {(p.kit || []).length} kit item{p.kit.length !== 1 ? "s" : ""}
        </div>
      )}

      <StarRating rating={p.rating || 0} count={p.review_count || p.endorsements_count || 0} />

      {/* Track-record chips: reviews + jobs completed */}
      <div className="flex flex-wrap gap-2 text-[11px] font-manrope" data-testid={`prof-track-${p.secusource_id}`}>
        {(p.review_count || 0) > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 font-semibold">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            {Number(p.rating || 0).toFixed(1)}/5 · {p.review_count} review{p.review_count === 1 ? "" : "s"}
          </span>
        )}
        {(p.completed_jobs || 0) > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gold-50 text-gold-600 border border-gold-200 px-2 py-0.5 font-semibold">
            <ClipboardCheck className="w-3 h-3" />
            {p.completed_jobs} job{p.completed_jobs === 1 ? "" : "s"} completed
          </span>
        )}
      </div>

      {p.quality_badges && p.quality_badges.length > 0 && (
        <QualityBadges badges={p.quality_badges} hideAdminOnly={true} />
      )}

      {p.match && <MatchScoreCard match={p.match} size="sm" showBreakdown={false} />}

      <Link
        to={`${linkPrefix}/${p.secusource_id}${search}`}
        className="mt-1 inline-flex items-center justify-center bg-brand hover:bg-brand-hover text-white rounded-md py-2 px-4 text-sm font-manrope font-semibold"
        data-testid={`view-prof-${p.secusource_id}`}
      >
        View & Request Booking
      </Link>
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "brand", testId }) {
  const toneMap = { brand: "icon-wrap", amber: "icon-wrap icon-amber", emerald: "icon-wrap icon-emerald", violet: "icon-wrap icon-violet", cyan: "icon-wrap icon-cyan", rose: "icon-wrap icon-rose" };
  return (
    <div className="stat-hero" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className={toneMap[accent] || "icon-wrap"}>
          {Icon && <Icon className="w-5 h-5" strokeWidth={2} />}
        </div>
      </div>
      <div className="num num-sm mt-4">{value}</div>
      <div className="eyebrow mt-2">{label}</div>
    </div>
  );
}

export function StatusPill({ status }) {
  const cls = status === "approved" ? "pill-approved" : status === "rejected" ? "pill-rejected" : "pill-pending";
  return <span className={`pill ${cls}`} data-testid={`status-${status}`}>{status}</span>;
}

export function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8">
      {eyebrow && <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">{eyebrow}</div>}
      <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white tracking-tight">{title}</h2>
      {subtitle && <p className="text-white/70 mt-3 max-w-2xl font-manrope">{subtitle}</p>}
    </div>
  );
}
